/**
 * Shared ServiceBox transfer helpers.
 *
 * Single source of truth for turning an ordre into a ServiceBox `CreateRdvParams`
 * payload and for validating that an ordre is transferable. Used by both the
 * single-ordre transfer UI (ServiceBoxTransferModal) and the batch pre-transfer
 * admin screen so the two flows can never drift.
 */
import type { Ordre } from "types/ordre";
import type { Dossier, DossierType } from "types/dossier";
import type { OrgServiceBoxSite } from "types/org";
import type { CreateRdvParams, AgendaOption, ServiceBoxCredentials } from "@services/serviceboxProxy";
import { isAPrevoir } from "types/ordre";

/** Map common marque strings to a normalised key for matching against sites. */
export function normaliseMarque(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .trim();
}

/**
 * Resolve the ServiceBox brand *code* (`rdvDto.rdvMarque`) from a vehicle's marque libellé.
 * ServiceBox files the RDV under the brand this code names — NOT the free-text `marque_libelle`
 * ("Fiat", "Renault"…), which is only a display label. If unset, the proxy defaults to "AC"
 * (Citroën), which is why non-Stellantis vehicles were landing as CITROEN.
 *
 * Rule: Citroën → AC, Opel → OV, Peugeot → AP, anything else → AP (Peugeot).
 * (Brand codes confirmed from the ServiceBox create form: rdvDto.rdvMarque = AP/AC/OV.)
 */
export function marqueCode(rawMarque: string | undefined): string {
  const key = normaliseMarque(rawMarque || "");
  if (key.includes("CITROEN")) return "AC";
  if (key.includes("OPEL")) return "OV";
  return "AP"; // Peugeot + everything else
}

export function detectSite(
  sites: OrgServiceBoxSite[] | undefined,
  vehicleMarque: string | undefined,
): OrgServiceBoxSite | undefined {
  if (!sites?.length || !vehicleMarque) return undefined;
  const key = normaliseMarque(vehicleMarque);
  return sites.find((s) => normaliseMarque(s.marque) === key);
}

/** Normalise an intervention label/name for matching ordre interventions against the catalog. */
export function normaliseLabel(raw: string): string {
  return raw.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/** Split a free-text French address into {rue, cp, ville} for the DMS client payload.
 *  Heuristic: a 5-digit code postal separates the street from the city. */
export function parseAddress(adresse?: string): { rue: string; cp: string; ville: string } {
  const raw = (adresse || "").trim();
  if (!raw) return { rue: "", cp: "", ville: "" };
  const m = raw.match(/^(.*?)[\s,]*\b(\d{5})\b[\s,]*(.*)$/);
  if (m) return { rue: m[1].replace(/[,\s]+$/, "").trim(), cp: m[2], ville: m[3].trim() };
  return { rue: raw, cp: "", ville: "" };
}

export function formatDateYYYYMMDD(ts?: { seconds: number }): string {
  if (!ts) return "";
  const d = new Date(ts.seconds * 1000);
  return d.getFullYear().toString() +
    (d.getMonth() + 1).toString().padStart(2, "0") +
    d.getDate().toString().padStart(2, "0");
}

export function formatTimeHHMM(ts?: { seconds: number }): string {
  if (!ts) return "0900";
  const d = new Date(ts.seconds * 1000);
  return d.getHours().toString().padStart(2, "0") +
    d.getMinutes().toString().padStart(2, "0");
}

/**
 * Resolve the ServiceBox réceptionnaire (planning column = a person) for an ordre from
 * its réception category, via the org's Réceptions mapping (Paramètres → Réceptions,
 * each carrying `servicebox_receptionnaire_id`). Returns "" when unmapped so the caller
 * can fall back to a default. Mirrors ServiceBoxTransferModal's auto-default logic.
 */
export function resolveReceptionnaireId(
  category: string | undefined | null,
  orgReceptions: { id: string; servicebox_receptionnaire_id?: string }[] | undefined,
  receptionnaires: AgendaOption[],
): string {
  if (!category) return "";
  const rec = (orgReceptions || []).find((r) => r.id === category);
  const sid = rec?.servicebox_receptionnaire_id;
  return sid && receptionnaires.some((r) => r.id === sid) ? sid : "";
}

/** Translate raw ServiceBox validation errors into actionable French messages.
 *  Shared with ServiceBoxTransferModal's inline copy (display context optional). */
export function humanizeServiceboxError(raw?: string, ctx?: { reception?: string; restitution?: string }): string {
  if (!raw) return "Erreur inconnue";
  if (/CCS indisponible/i.test(raw)) {
    const t = [
      ctx?.reception ? `réception ${ctx.reception}` : null,
      ctx?.restitution ? `restitution ${ctx.restitution}` : null,
    ].filter(Boolean).join(" / ");
    return `Créneau refusé par ServiceBox${t ? ` (${t})` : ""} : « CCS indisponible ». La colonne du réceptionnaire est probablement déjà occupée à cette heure, ou l'horaire est hors des plages ServiceBox (avant le premier créneau, après le dernier, ou hors d'un pas de 7,5 min).`;
  }
  if (/Mod[èe]le est obligatoire/i.test(raw)) {
    return "Le modèle du véhicule est obligatoire côté ServiceBox. Complétez la fiche véhicule.";
  }
  if (/T[ée]l[ée]phone mobile est obligatoire/i.test(raw)) {
    return "Téléphone mobile obligatoire côté ServiceBox — le client n'a pas de mobile renseigné.";
  }
  return raw;
}

/** A safe default ServiceBox réceptionnaire: the first org réception carrying a
 *  servicebox_receptionnaire_id that also exists in the agenda options (a real bookable
 *  column like RECEPTION 1). Preferred over the raw first agenda option, which may be a
 *  non-bookable personnel (TAKATA, carrosserie, …) and trigger "CCS indisponible". */
export function firstLinkedReceptionnaireId(
  orgReceptions: { servicebox_receptionnaire_id?: string }[] | undefined,
  receptionnaires: AgendaOption[],
): string {
  const linked = (orgReceptions || [])
    .map((r) => r.servicebox_receptionnaire_id)
    .find((id) => id && receptionnaires.some((rr) => rr.id === id));
  return linked || receptionnaires[0]?.id || "";
}

export interface TransferValidation {
  missingFields: string[];
  datesOutOfOrder: boolean;
  canTransfer: boolean;
}

/** Resolve the ServiceBox point-de-vente (pdv_id) for an ordre's vehicle brand, matching
 *  ServiceBoxTransferModal: the vehicle's site, else the Peugeot site, else the org default. */
export function resolvePdvId(
  sites: OrgServiceBoxSite[] | undefined,
  vehicleMarque: string | undefined,
  fallbackSiteCode?: string,
): string {
  const autoSite = detectSite(sites, vehicleMarque);
  const peugeotSite = (sites || []).find(
    (s) => normaliseMarque(s.marque).includes("PEUG") || normaliseMarque(s.label).includes("PEUG"),
  );
  return autoSite?.pdv_id || peugeotSite?.pdv_id || fallbackSiteCode || "";
}

/** Block transfer when essential info is missing — otherwise ServiceBox would receive an
 *  empty/invalid OR. Mirrors ServiceBoxTransferModal's `missingFields`/`canTransfer`.
 *  `requireDossierType` can be turned off for the batch flow, which doesn't load each
 *  ordre's parent dossier (the dossier type isn't part of the proxy payload). */
export function validateOrdreForTransfer(
  ordre: Ordre,
  dossierType?: DossierType,
  opts?: { requireDossierType?: boolean },
): TransferValidation {
  const missingFields: string[] = [];
  if (opts?.requireDossierType !== false && !dossierType) missingFields.push("Type de dossier");
  if (!(ordre.vehicle?.modele || ordre.modele)) missingFields.push("Modèle du véhicule");
  if (!(ordre.immatriculation || ordre.vehicle?.plate)) missingFields.push("Immatriculation");
  // Only "inclus" interventions are billed on this RDV; "à prévoir" lines belong on the dossier.
  if (!ordre.interventions || ordre.interventions.filter((i) => !isAPrevoir(i)).length === 0) {
    missingFields.push("Au moins une intervention incluse");
  }
  if (!ordre.reception_date?.seconds) missingFields.push("Date de réception");
  if (!ordre.restitution?.seconds) missingFields.push("Date de restitution");
  const datesOutOfOrder =
    !!ordre.reception_date?.seconds &&
    !!ordre.restitution?.seconds &&
    ordre.restitution.seconds < ordre.reception_date.seconds;
  return { missingFields, datesOutOfOrder, canTransfer: missingFields.length === 0 && !datesOutOfOrder };
}

/** Build the ServiceBox `travaux` work-line array from the ordre's included interventions. */
export function buildTravaux(ordre: Ordre, forfaitByLabel: Record<string, string>): CreateRdvParams["travaux"] {
  // ServiceBox intervention "type": "5" = Forfait Local when a forfait code is present
  // (denormalised on the ordre, or resolved from the catalog by name), otherwise left
  // empty so ServiceBox defaults to "Standard" (1). Exclude "à prévoir" lines.
  return ordre.interventions.filter((i) => !isAPrevoir(i)).map((i) => {
    const forfait = i.forfait_code || forfaitByLabel[normaliseLabel(i.label || "")];
    return {
      nom: i.label || "Intervention",
      duree: (i.estimated_hours || 0.5).toFixed(2),
      ...(forfait ? { type: "5", forfait_code: forfait } : {}),
    };
  });
}

export interface BuildCreateRdvParamsArgs {
  ordre: Ordre;
  /** The ordre's parent dossier, if loaded — its primary contact is authoritative for client data. */
  parentDossier?: Dossier | null;
  creds: ServiceBoxCredentials;
  receptionnaireId: string;
  restitutionReceptionnaireId?: string;
  equipeId: string;
  pdvId: string;
  /** Catalog forfait codes keyed by normalised intervention name (fallback for older ordres). */
  forfaitByLabel?: Record<string, string>;
}

/**
 * Build the exact `CreateRdvParams` payload the proxy expects. Kept byte-for-byte in step
 * with ServiceBoxTransferModal.handleTransfer — the recovery batch must produce the same
 * payload the working single-transfer produces. Does NOT set skip_alpha; the caller picks
 * createRdv (with OR) vs createRdvServiceBoxOnly (pre-transfer, no OR).
 */
export function buildCreateRdvParams({
  ordre,
  parentDossier,
  creds,
  receptionnaireId,
  restitutionReceptionnaireId,
  equipeId,
  pdvId,
  forfaitByLabel = {},
}: BuildCreateRdvParamsArgs): CreateRdvParams {
  const civiliteMap: Record<string, string> = { "M.": "1", "Mme": "2", "Mlle": "3" };

  // Carvis is authoritative for the client. Prefer the canonical dossier contact (latest
  // edit) over the ordre's denormalised copy.
  const primaryContact = ordre.contacts?.find((c) => c.is_primary) || ordre.contacts?.[0];
  const dossierPrimary = parentDossier?.contacts?.find((c) => c.is_primary) || parentDossier?.contacts?.[0];
  const addr = parseAddress(dossierPrimary?.adresse || primaryContact?.adresse);
  const vehicle = ordre.vehicle;

  return {
    ...creds,
    date: formatDateYYYYMMDD(ordre.reception_date),
    heure: formatTimeHHMM(ordre.reception_date),
    restitution_date: formatDateYYYYMMDD(ordre.restitution),
    restitution_heure: formatTimeHHMM(ordre.restitution),
    receptionnaire_id: receptionnaireId,
    restitution_receptionnaire_id: restitutionReceptionnaireId || receptionnaireId,
    equipe_id: equipeId,
    // Already on ServiceBox → update the existing RDV (avoids the duplicate-create
    // "CCS indisponible" conflict and pushes updated client data).
    ...(ordre.servicebox_rdv_id ? { rdv_id: ordre.servicebox_rdv_id } : {}),
    ...(ordre.servicebox_dossier_id ? { dossier_id: ordre.servicebox_dossier_id } : {}),
    nom: dossierPrimary?.nom || primaryContact?.nom || ordre.name || "",
    prenom: dossierPrimary?.prenom || primaryContact?.prenom || "",
    tel_mobile: (dossierPrimary?.phone || primaryContact?.phone || "").replace(/\s/g, ""),
    email: dossierPrimary?.email || primaryContact?.email || "",
    civilite_id: civiliteMap[dossierPrimary?.civilite || primaryContact?.civilite || "M."] || "1",
    adresse_rue: addr.rue,
    adresse_code_postal: addr.cp,
    adresse_ville: addr.ville,
    immatriculation: ordre.immatriculation || vehicle?.plate || "",
    vin: vehicle?.vin || "",
    // Brand *code* that ServiceBox files the RDV under (rdvDto.rdvMarque). Without this the
    // proxy defaults to "AC" (Citroën), so every vehicle was created as CITROEN.
    marque: marqueCode(vehicle?.marque),
    marque_libelle: vehicle?.marque || "",
    ldp_libelle: vehicle?.modele || ordre.modele || "",
    kilometrage: vehicle?.km?.toString() || ordre.booking_request?.km || "",
    travaux: buildTravaux(ordre, forfaitByLabel),
    pdv_id: pdvId || "011622H",
  };
}
